/**
 * Shared password sign-in state machine used by both the web `/login` page
 * and the native `MobileLoginScreen`. Owns:
 *
 *   - Loading state during sign-in and MFA verify
 *   - MFA branch detection + factor id stashing
 *   - The user-id stash so MFA verify doesn't need to re-query auth.uid()
 *
 * The page-specific bits (where to redirect on success, whether to show a
 * "Welcome back" toast, how to log errors) come in via `options`. This keeps
 * the duplicated auth flow logic in one file while letting each page own its
 * own UX choices.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MFA_VERIFY_ERROR_KEYS, classifyMfaVerifyError } from '@/components/auth/mfa-verify-error';
import {
  PASSWORD_SIGN_IN_ERROR_KEYS,
  classifyPasswordSignInError,
  type PasswordSignInErrorKey,
} from '@/components/auth/password-sign-in-error';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { checkMFARequirementStrict, isLockContentionError, verifyMFACode } from '@/lib/auth-utils';
import { getSupabaseAuthTokenKey } from '@/lib/mobile/auth-persistence';
import { getSupabaseAuthStorage } from '@/lib/mobile/secure-storage-adapter';

export interface PasswordSignInValues {
  email: string;
  password: string;
}

/**
 * Advisory state for the password path. `check_failed` means a session exists
 * but whether it still owes a TOTP step-up could not be determined - the one
 * state that must not be resolved by letting the user through.
 */
export type PasswordSignInNotice = 'none' | 'check_failed';

export interface UsePasswordSignInOptions {
  /** Called once the password (and MFA, if required) flow completes successfully. */
  onSignedIn: (userId: string) => void | Promise<void>;
  /** Optional context-tagged error log. Defaults to no-op. */
  logError?: (label: string, error: unknown) => void;
  /**
   * Receives the classified failure INSTEAD of the default destructive toast,
   * for surfaces that render sign-in failures inline. Either way the raw
   * Supabase message never reaches the UI.
   */
  onSignInError?: (key: PasswordSignInErrorKey) => void;
}

/**
 * How long dismissMFA waits for the scope-local signOut before force-clearing
 * the persisted session key anyway. Long enough for the normal revoke round
 * trip; short enough that a slow or offline network cannot keep the aal1
 * session sitting in storage while the MFA view is already gone.
 */
const SIGN_OUT_REVOKE_GRACE_MS = 2000;

export interface UsePasswordSignInResult {
  isLoading: boolean;
  showMFAInput: boolean;
  userEmail: string;
  /** Drives the inline "we could not check" copy and its Retry affordance. */
  notice: PasswordSignInNotice;
  signInWithPassword: (values: PasswordSignInValues) => Promise<void>;
  /**
   * Re-runs a step-up check that could not answer. Needs no re-entered
   * password: the session from the first attempt is still live, deliberately
   * kept so a blip costs a tap rather than the whole credential again.
   */
  retryStepUpCheck: () => Promise<void>;
  /**
   * Drops the advisory notice and nothing else, for a host reopening a
   * dismissed sign-in surface. Session teardown belongs to `abandonSignIn`;
   * wanting a clean slate visually is not a request to touch a session.
   */
  clearNotice: () => void;
  verifyMFA: (code: string) => Promise<void>;
  /**
   * Call-and-forget, matching `MfaStepUpController`. Asynchronous inside - the
   * sign-out completes before the form is allowed back - but deliberately not
   * awaitable from outside, so no UI can block on a teardown. The passkey
   * hook already stated this was true of both hooks; now it is.
   */
  dismissMFA: () => void;
  /** See `MfaStepUpController.abandonSignIn` in `use-passkey-sign-in.ts`. */
  abandonSignIn: () => void;
}

export function usePasswordSignIn(options: UsePasswordSignInOptions): UsePasswordSignInResult {
  const [isLoading, setIsLoading] = useState(false);
  const [showMFAInput, setShowMFAInput] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [signedInUserId, setSignedInUserId] = useState<string | null>(null);
  const [notice, setNotice] = useState<PasswordSignInNotice>('none');
  const { toast } = useToast();
  const { t } = useTranslation('auth');
  /**
   * True from the moment a session is persisted until the step-up over it is
   * discharged. A ref, not state: every consumer reads it from an async
   * continuation, where a render-scoped boolean is already a render stale.
   */
  const stepUpOwedRef = useRef(false);

  /**
   * The in-flight MFA-dismissal teardown, if any. Its global revoke and
   * storage purge destroy whatever session is CURRENT when they land - there
   * is no session-identity guard - so a sign-in that overlaps it has its
   * fresh aal1 session wiped, and the strict AAL check then fails closed
   * ("check_failed") instead of showing the step-up. A ref, not state: it is
   * only ever read from async continuations.
   *
   * Serialization narrows the hazard, not closes it: a revoke that outlives
   * the grace keeps floating after this promise resolves, and its late
   * continuation can still remove a successor session.
   * TODO(PUB-1028): port the identity-guarded teardown from
   * `passkey-session-teardown.ts` (revoke aimed at the captured session, not
   * "current") - carries a global-to-local blast-radius decision.
   */
  const teardownRef = useRef<Promise<void> | null>(null);

  /**
   * The single exit for every sign-in failure. Callers that render errors
   * inline take the classified cause; everyone else gets the same cause as a
   * destructive toast. Neither path can leak the provider's own wording.
   */
  const reportSignInError = (error: unknown) => {
    const key = classifyPasswordSignInError(error);
    if (options.onSignInError) {
      options.onSignInError(key);
      return;
    }
    toast({
      title: t('passwordSignIn.signInFailedTitle'),
      description: t(PASSWORD_SIGN_IN_ERROR_KEYS[key]),
      variant: 'destructive',
    });
  };

  /**
   * What happens between a persisted session and app entry. Split out of
   * `signInWithPassword` so a retry can re-run exactly this - the check and its
   * three outcomes - without a second credential submission.
   */
  const resolveStepUp = async (userId: string) => {
    const requirement = await checkMFARequirementStrict();

    if (requirement.status === 'indeterminate') {
      // FAIL CLOSED. The lossy `checkMFARequirement()` answers `needsMFA: false`
      // on every error path, so the previous code here walked a TOTP-enrolled
      // user straight into the app whenever the AAL lookup blipped - over a
      // session already persisted, which RLS admits because it is AAL-blind
      // (ADR 0068). The session is KEPT rather than revoked so the retry
      // re-reads the level without re-entering the password.
      //
      // The session is not contained meanwhile: the route guard reads the lossy
      // variant, so the same failure resolves there as "nothing owed" and
      // admits a direct navigation. That residual is ADR 0068's, not this
      // block's - this is the strict gate on the password path, mirroring
      // `use-passkey-sign-in.ts`.
      stepUpOwedRef.current = true;
      setNotice('check_failed');
      setIsLoading(false);
      return;
    }

    if (requirement.status === 'required') {
      // Armed HERE rather than left to the caller. `signInWithPassword` arms it
      // before the lookup, but `retryStepUpCheck` DISARMS it on entry - so
      // relying on the caller leaves a retry that lands on `required` showing
      // the step-up over a live aal1 session with the obligation reading false,
      // and `abandonSignIn` would then let a sheet dismissal walk past that
      // session instead of revoking it. Every branch below owns the obligation
      // outright, which is what makes this function safe from either caller.
      stepUpOwedRef.current = true;
      setMfaFactorId(requirement.factorId);
      setShowMFAInput(true);
      setIsLoading(false);
      return;
    }

    // Discharged: no step-up is owed, so the session is complete. Released
    // BEFORE `onSignedIn` so a failure inside it cannot leave a finished
    // session still looking owed to a later teardown. Set rather than assumed:
    // a retry after a `check_failed` arrives here with the obligation armed.
    stepUpOwedRef.current = false;
    await options.onSignedIn(userId);
    // Reset loading after onSignedIn returns. If the page navigated, this
    // setState is a no-op on the unmounted component. If onSignedIn handled
    // an error without navigating (e.g. completeLogin reported a problem and
    // surfaced a toast), the user is still on the login screen and the
    // submit button must re-enable. Without this, the button stays
    // disabled/spinning indefinitely after the destructive toast.
    setIsLoading(false);
  };

  const signInWithPassword = async (values: PasswordSignInValues) => {
    setIsLoading(true);
    setNotice('none');
    try {
      // Serialize behind any in-flight dismissal: the teardown is bounded (the
      // revoke gets SIGN_OUT_REVOKE_GRACE_MS before the storage purge runs),
      // so this waits ~2s at worst rather than racing the purge with a fresh
      // session. Never rejects - dismissMFAAndSignOut handles its own errors.
      // Cleared before the await, not after: if the teardown ever rejects, a
      // sticky ref would make every later sign-in re-await the same rejected
      // promise. The local still gets awaited either way.
      const teardown = teardownRef.current;
      if (teardown !== null) {
        teardownRef.current = null;
        await teardown;
      }
      const { error, data } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        reportSignInError(error);
        setIsLoading(false);
        return;
      }
      // A response with neither error nor session is a sign-in that did not
      // happen; without this the submit button would just re-enable in silence.
      if (!data.session || !data.user) {
        reportSignInError(null);
        setIsLoading(false);
        return;
      }

      setSignedInUserId(data.user.id);
      // Stashed unconditionally, before the outcome is known: a `check_failed`
      // that later retries into `required` mounts the step-up view straight
      // from state, with no credential left anywhere to re-read it from.
      setUserEmail(values.email);
      // Armed HERE, before the AAL lookup rather than beside `setShowMFAInput`:
      // the lookup is a network round trip during which a live aal1 session
      // already exists with no step-up view over it. A teardown gated on the
      // view would miss that whole window.
      stepUpOwedRef.current = true;
      await resolveStepUp(data.user.id);
    } catch (error) {
      if (isLockContentionError(error)) {
        // Contention is not a failure worth naming, but the form still has to
        // come back: nothing else clears this flag, because this hook never
        // subscribes to auth state changes. Left set, the submit button spins
        // for good and the user cannot even retry.
        setIsLoading(false);
        return;
      }
      options.logError?.('signInWithPassword', error);
      reportSignInError(error);
      setIsLoading(false);
    }
  };

  const retryStepUpCheck = async () => {
    if (signedInUserId === null) {
      return;
    }
    setNotice('none');
    setIsLoading(true);
    // Handed to the check: `resolveStepUp` re-arms the obligation itself if a
    // step-up turns out to still be owed.
    stepUpOwedRef.current = false;
    try {
      await resolveStepUp(signedInUserId);
    } catch (error) {
      // Contention is not worth naming to the user, so it skips the log - but it
      // settles NOTHING, so it must restore the state this function cleared on
      // entry, exactly like the generic branch below. Returning early instead
      // leaves the worst of both: the notice gone (so no Retry to press), the
      // user not signed in, and `stepUpOwedRef` still false - which is the half
      // that matters, because a dismissal would then walk past the live aal1
      // session rather than tearing it down.
      if (!isLockContentionError(error)) {
        options.logError?.('retryStepUpCheck', error);
      }
      // Back to the advisory state rather than a sign-in failure toast: the
      // credentials were never in question, and the session is still live and
      // still owed.
      stepUpOwedRef.current = true;
      setNotice('check_failed');
      setIsLoading(false);
    }
  };

  const clearNotice = (): void => {
    setNotice('none');
  };

  const verifyMFA = async (code: string) => {
    if (!mfaFactorId) {
      return;
    }
    // This call owns the obligation from here: the branches below settle it by
    // outcome, and a teardown racing them could revoke the very session a
    // verified code is about to complete as aal2.
    stepUpOwedRef.current = false;
    setIsLoading(true);
    // Loading resets regardless of what `onSignedIn` did - including THROWING,
    // which is why this is a `finally` and not a line in each branch. If the
    // page navigated it is a no-op on an unmounted component; if `onSignedIn`
    // failed without navigating, the user is still on the step-up view and
    // needs the button back.
    //
    // Safe as a bare `finally` with no obligation hand-back: `verifyMFACode`
    // catches everything and always returns a result, so the ONLY rejection
    // reaching here is `onSignedIn`'s, and that path has already made the
    // session aal2 and complete. If that ever stops being true, this needs a
    // `catch` that re-arms.
    try {
      const result = await verifyMFACode(mfaFactorId, code);
      if (result.success) {
        if (signedInUserId) {
          await options.onSignedIn(signedInUserId);
        }
      } else {
        // Hand the obligation back: the step-up view stays up over the same aal1
        // session, so a dismissal from here still owes a teardown.
        stepUpOwedRef.current = true;
        toast({
          title: t('passwordSignIn.verificationFailedTitle'),
          description: t(MFA_VERIFY_ERROR_KEYS[classifyMfaVerifyError(result.error)]),
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * A failed global revoke returns BEFORE auth-js drops its own copy of the
   * session, so the client keeps a live in-memory token: removing the storage
   * key alone leaves every request in this process authenticated even though
   * nothing survives a reload. The local scope drops it with no round trip.
   */
  const clearLocalSession = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      options.logError?.('dismissMFALocal', error);
    }
  };

  const dismissMFAAndSignOut = async () => {
    // Backing out of the TOTP step-up must drop the aal1 session that
    // signInWithPassword persisted - resetting UI state alone leaves a live
    // session in storage that reaches the app without completing MFA. Same
    // semantics as handleBack in pages/mfa-verify.tsx. Never rejects: the UI
    // wires this to void-typed back handlers.
    //
    // UI state resets first so the Back button never blocks on the network;
    // the session teardown below continues regardless.
    stepUpOwedRef.current = false;
    setSignedInUserId(null);
    setShowMFAInput(false);
    setMfaFactorId(null);
    setUserEmail('');
    // The retry affordance goes with the session it would have retried against.
    setNotice('none');
    // signOut() must START before the storage fallback: it reads the persisted
    // session to obtain the token it revokes server-side, so removing the key
    // first would silently skip revocation. It only gets a bounded grace to
    // finish - a slow or offline revoke must not keep the aal1 session in
    // storage after the MFA view is gone. Past the grace, the key is cleared
    // while revocation continues in the background (its errors stay handled
    // here, so the floating promise never rejects unhandled).
    const revocation = (async () => {
      try {
        // Scope local: revoke the abandoned in-flight session only. Bare
        // signOut() is scope GLOBAL and would kill the user's other devices
        // for backing out of a login attempt.
        const { error } = await supabase.auth.signOut({ scope: 'local' });
        if (error) {
          options.logError?.('dismissMFA', error);
        }
      } catch (error) {
        options.logError?.('dismissMFA', error);
      }
    })();
    let graceTimer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      revocation,
      new Promise<void>((resolve) => {
        graceTimer = setTimeout(resolve, SIGN_OUT_REVOKE_GRACE_MS);
      }),
    ]);
    clearTimeout(graceTimer);
    // Whatever the revoke did - succeeded, failed, or is still hanging past the
    // grace - the client must not keep a usable session in memory. On a clean
    // revoke auth-js has already dropped it and this is a no-op; on every other
    // path it is the only step that does.
    //
    // NOT awaited: a hung global sign-out still holds auth-js's lock, so
    // awaiting this would block the storage removal below - the one step that
    // guarantees the session cannot survive a reload. Its errors are handled
    // inside, so the floating promise never rejects unhandled.
    void clearLocalSession();
    // signOut() only removes the persisted session after server revocation
    // succeeds (auth-js returns early on failure, any scope), so a failed,
    // offline, or still-running revoke would leave the aal1 session in
    // storage. Removing the key through the client's own storage adapter
    // guarantees the session cannot survive a reload regardless of the API
    // outcome. No unmount cleanup is needed: the continuation only touches
    // storage, never React state.
    try {
      await getSupabaseAuthStorage().removeItem(
        getSupabaseAuthTokenKey(String(import.meta.env.VITE_SUPABASE_URL)),
      );
    } catch (error) {
      options.logError?.('dismissMFA', error);
    }
  };

  /**
   * No ceremony to abort on this path - the credential POST is a plain fetch
   * with no OS surface behind it - so this is the obligation half only.
   */
  const abandonSignIn = (): void => {
    if (!stepUpOwedRef.current) {
      return;
    }
    dismissMFA();
  };

  const dismissMFA = (): void => {
    teardownRef.current = dismissMFAAndSignOut();
  };

  return {
    isLoading,
    showMFAInput,
    userEmail,
    notice,
    signInWithPassword,
    retryStepUpCheck,
    clearNotice,
    verifyMFA,
    dismissMFA,
    abandonSignIn,
  };
}
